/* @flow */
import * as React from 'react';
import { compose, graphql } from 'react-apollo';
import { isEmpty } from 'lodash';
import idx from 'idx';
import update from 'immutability-helper';

import ToastActions from 'actions/ToastActions';
import ErrorReporter from 'app/components/ErrorReporter';
import Notice from 'components/Notice';
import Text from 'components/Text';

import PendingRolesTable from 'roleRequests/components/PendingRolesTable';

import ROLE_REQUESTS_QUERY from './queries/RoleRequestsQuery.gql';
import APPROVE_ROLE_REQUESTS_MUTATION from './mutations/ApproveRoleRequests.gql';

import type { RoleRequests_organizations_roleRequests_roleRequests } from 'roleRequests/containers/queries/__generated__/RoleRequests';
import type { ApproveRoleRequestsInput } from 'roleRequests/containers/mutations/__generated__/approveRoleRequests';
import type { RoleRequest } from 'roleRequests/components/PendingRolesTable';
import type { errorFragment } from 'queries/__generated__/errorFragment';

type Props = {
  organizationUuid: string,
  userRole: string,
  roleRequests: RoleRequests_organizations_roleRequests_roleRequests[],
  hasNextPage: boolean,
  fetchMore: () => Promise<void>,
  loading: boolean,
  error: errorFragment,
  refetch: (?{}) => Promise<void>,
  reloadQuery: boolean,
  onPersonClicked: RoleRequest => void,
  openUserId: ?number,
  approveRoleRequests: ({ variables: { input: ApproveRoleRequestsInput } }) => Promise<void>,
  pageHeader: React.Element<any>,
  pageHeaderHeight: number,
};

class PendingRolesTableContainer extends React.Component<Props> {
  static defaultProps = {
    roleRequests: [],
  };

  componentDidUpdate(prevProps: Props) {
    const { reloadQuery, refetch } = this.props;

    if (reloadQuery && !prevProps.reloadQuery) {
      refetch();
    }
  }

  handleApproveClicked = (uuid: string) => {
    const { approveRoleRequests, organizationUuid, refetch, roleRequests, userRole } = this.props;

    approveRoleRequests({ variables: { input: { organizationUuid, uuids: [uuid] } } })
      .then(() => {
        const roleRequest = roleRequests.find(r => r.uuid === uuid);

        if (roleRequest) {
          const { firstName, lastName } = roleRequest.user;
          ToastActions.showSuccessToast(
            `${[firstName, lastName].join(' ')} was approved as a ${userRole}`
          );
        } else {
          ToastActions.showSuccessToast(`User was approved as a ${userRole}`);
        }

        refetch();
      })
      .catch(() => {
        const roleRequest = roleRequests.find(r => r.uuid === uuid);

        if (roleRequest) {
          const { firstName, lastName } = roleRequest.user;
          ToastActions.showErrorToast(
            `Unable to approve ${[firstName, lastName].join(
              ' '
            )}'s' request to be a ${userRole}. Please try again later`
          );
        } else {
          ToastActions.showErrorToast(
            `Unable to approve the user's request to be a ${userRole}. Please try again later.`
          );
        }
      });
  };

  handleDenial = () => {
    this.props.refetch();
  };

  translateRoleRequests = (
    roleRequests: RoleRequests_organizations_roleRequests_roleRequests[]
  ): RoleRequest[] => {
    // $FlowFixMe
    return roleRequests.map(roleRequest => {
      const { user } = roleRequest;

      const emailDevices = user.adminVisibleDeviceDetails
        .filter(device => {
          return device.type === 'email';
        })
        .map(e => e.endpoint)
        .join(',');

      const email = isEmpty(emailDevices) ? null : emailDevices;

      return {
        uuid: roleRequest.uuid,
        user: {
          id: user.id,
          uuid: user.uuid,
          firstName: user.firstName,
          lastName: user.lastName,
          email,
          profilePictureUrl: user.profilePictureUrl,
          initials: user.initials,
          color: user.color,
          reachabilityInfo: user.reachabilityInfo,
        },
        createdAt: roleRequest.createdAt,
      };
    });
  };

  renderError = () => {
    ToastActions.showDefaultErrorToast();

    return (
      <Text align="center">
        <ErrorReporter error="PendingRolesTableContainer - couldn't load data" />
        <Notice color="white">
          Sorry—no data is available at this time. Try refreshing the page.
        </Notice>
      </Text>
    );
  };

  render() {
    const {
      fetchMore,
      error,
      hasNextPage,
      loading,
      openUserId,
      onPersonClicked,
      organizationUuid,
      roleRequests,
      userRole,
      pageHeader,
      pageHeaderHeight,
    } = this.props;

    if (error) {
      return this.renderError();
    }

    return (
      <PendingRolesTable
        organizationUuid={organizationUuid}
        userRole={userRole}
        roleRequests={this.translateRoleRequests(roleRequests)}
        fetchMore={fetchMore}
        hasNextPage={hasNextPage}
        loading={loading}
        onApproval={this.handleApproveClicked}
        onDenial={this.handleDenial}
        onPersonClicked={onPersonClicked}
        openUserId={openUserId}
        pageHeader={pageHeader}
        pageHeaderHeight={pageHeaderHeight}
      />
    );
  }
}

const withRoleRequests = graphql(ROLE_REQUESTS_QUERY, {
  options: ({ organizationUuid, userRole }) => {
    return {
      variables: {
        organizationUuid,
        role: userRole,
        first: 20,
        after: null,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data }) => ({
    fetchMore: () =>
      data.fetchMore({
        variables: {
          after: idx(data.organizations, _ => _[0].roleRequests.pageInfo.endCursor),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const fetchMoreContents = idx(fetchMoreResult.organizations, _ => _[0]);
          const prevContents = idx(prev.organizations, _ => _[0]);

          if (!fetchMoreContents || !prevContents) {
            return prev;
          }

          return update(prev, {
            organizations: {
              '0': {
                roleRequests: {
                  roleRequests: {
                    $set: [
                      ...prevContents.roleRequests.roleRequests,
                      ...fetchMoreContents.roleRequests.roleRequests,
                    ],
                  },
                  pageInfo: { $set: fetchMoreContents.roleRequests.pageInfo },
                },
              },
            },
          });
        },
      }),
    roleRequests: idx(data.organizations, _ => _[0].roleRequests.roleRequests),
    hasNextPage: idx(data.organizations, _ => _[0].roleRequests.pageInfo.hasNextPage),
    loading: data.loading,
    error: data.error,
    refetch: data.refetch,
  }),
});

const withApproveRoleRequestsMutation = graphql(APPROVE_ROLE_REQUESTS_MUTATION, {
  name: 'approveRoleRequests',
});

export { PendingRolesTableContainer };

export default compose(
  withRoleRequests,
  withApproveRoleRequestsMutation
)(PendingRolesTableContainer);
